import React, { useEffect, useState } from 'react';
import { gatewayApi } from '../services/gateway';
import SafeIcon, { HelpHint } from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { 
  FiSliders, FiShield, FiGlobe, FiClock, FiActivity, 
  FiAlertTriangle, FiSave, FiLock, FiEye, FiCpu, 
  FiSmartphone, FiServer, FiTrash2, FiToggleRight 
} = FiIcons;

const SettingsSection = ({ title, icon, children }) => (
  <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden mb-6">
    <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2 bg-slate-50/50">
      <SafeIcon icon={icon} className="text-slate-400" />
      <h3 className="font-bold text-slate-800 text-sm">{title}</h3>
    </div>
    <div className="p-6 space-y-5">
      {children}
    </div>
  </div>
);

const ToggleField = ({ label, description, enabled, onToggle }) => (
  <div className="flex items-center justify-between py-2">
    <div className="space-y-0.5">
      <div className="flex items-center gap-1"><p className="text-xs font-bold text-slate-700">{label}</p><HelpHint label={label}/></div>
      <p className="text-[10px] text-slate-500">{description}</p>
    </div>
    <button 
      onClick={onToggle}
      className={`w-10 h-5 rounded-full p-1 transition-all ${enabled ? 'bg-blue-600' : 'bg-slate-300'}`}
    >
      <div className={`w-3 h-3 bg-white rounded-full transition-all transform ${enabled ? 'translate-x-5' : 'translate-x-0'}`}></div>
    </button>
  </div>
);

const Settings = () => {
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [settings, setSettings] = useState({
    primaryProvider: 'Emailit',
    backupProvider: 'ZeptoMail',
    fallbackMode: 'Automatic',
    timezone: 'AEST (UTC+10)',
    senderName: 'BPA Gateway',
    senderEmail: 'no-reply@bpasolutions.au',
    adminEmail: 'it-alerts@bpasolutions.au',
    dailyLimit: 10000,
    monthlyLimit: 250000,
    maintenanceMode: false,
    debugMode: true,
    retentionDays: 90,
    maskEmails: true,
    maskSecrets: true,
    maskLLM: true,
    defaultChatbot: false,
    otpMode: 'BPA Verify',
    vaultLockMins: 5,
    requirePinReveal: true,
    requirePinKeys: true
  });

  const update = (key, val) => setSettings(prev => ({ ...prev, [key]: val }));

  useEffect(() => {
    gatewayApi.listSettings().then(res => {
      const entry = (res.data || []).find(x => x.setting_key === 'mail_ui_preferences');
      if(entry) setSettings(old => ({...old,...JSON.parse(entry.setting_value)}));
    }).catch(err => setSaveError(err.message));
  }, []);

  const handleSave = async () => {
    setIsSaving(true);setSaveError('');
    try {
      await gatewayApi.saveSetting({
        setting_key:'mail_ui_preferences',
        setting_value:JSON.stringify(settings),
        value_type:'json'
      });
      window.alert('Preferences saved. Provider routing and security enforcement remain server-managed.');
    } catch(err) {setSaveError(err.message || 'Save failed');}
    finally {setIsSaving(false);}
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-in slide-in-from-bottom-4 duration-500 pb-20">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
            <SafeIcon icon={FiSliders} className="text-blue-600" /> Global Gateway Settings
          </h2>
          <p className="text-sm text-slate-500">Configure core routing, security, and default behaviors.</p>
        </div>
        <button 
          onClick={handleSave}
          disabled={isSaving}
          className="px-6 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 transition-all shadow-md flex items-center gap-2 disabled:opacity-50"
        >
          <SafeIcon icon={isSaving ? FiClock : FiSave} className={isSaving ? 'animate-spin' : ''} />
          {isSaving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {saveError && <p role="alert" className="rounded-lg p-3 text-red-700 bg-red-50">{saveError}</p>}
      <p className="text-xs text-slate-500">These are saved interface preferences. Mail security, sender restrictions and dispatch limits are enforced by the backend configuration.</p>
      {/* Security Warning */}
      <div className="bg-slate-900 text-white rounded-2xl p-5 flex items-start gap-4 shadow-xl border border-slate-800">
        <SafeIcon icon={FiShield} className="text-emerald-400 text-xl shrink-0 mt-1" />
        <div className="space-y-1">
          <h4 className="text-sm font-bold">Security & Encryption Policy</h4>
          <p className="text-[11px] text-slate-400 leading-relaxed">
            Provider secrets, LLM secrets, and master credentials are <b>not stored</b> directly in the frontend. 
            They are protected through Secure Vault backend encryption and comprehensive audit logging. 
            Access to sensitive values requires administrative PIN verification.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Routing & Providers */}
        <SettingsSection title="Routing & Providers" icon={FiServer}>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Fallback Mode</label><HelpHint label="Fallback Mode"/>
              <div className="grid grid-cols-2 gap-2">
                {['Automatic', 'Manual'].map(mode => (
                  <button 
                    key={mode} 
                    onClick={() => update('fallbackMode', mode)}
                    className={`py-2 text-xs font-bold rounded-lg border transition-all ${settings.fallbackMode === mode ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-500 border-slate-200'}`}
                  >{mode}</button>
                ))}
              </div>
            </div>
            <ToggleField 
              label="Maintenance Mode" 
              description="Reject all non-admin requests with 503"
              enabled={settings.maintenanceMode}
              onToggle={() => update('maintenanceMode', !settings.maintenanceMode)}
            />
          </div>
        </SettingsSection>

        {/* Security Controls */}
        <SettingsSection title="Vault & Privacy" icon={FiLock}>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Vault Auto-Lock (Minutes)</label><HelpHint label="Vault Auto-Lock (Minutes)"/>
              <input 
                type="number" 
                className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm"
                value={settings.vaultLockMins}
                onChange={(e) => update('vaultLockMins', e.target.value)}
              />
            </div>
            <ToggleField 
              label="Require PIN for Secrets" 
              description="Ask for PIN to reveal Vault values"
              enabled={settings.requirePinReveal}
              onToggle={() => update('requirePinReveal', !settings.requirePinReveal)}
            />
          </div>
        </SettingsSection>

        {/* Global Limits */}
        <SettingsSection title="Global Quotas" icon={FiActivity}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Daily Cap</label><HelpHint label="Daily Cap"/>
                <input type="number" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm" value={settings.dailyLimit} onChange={(e) => update('dailyLimit', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Monthly Cap</label><HelpHint label="Monthly Cap"/>
                <input type="number" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm" value={settings.monthlyLimit} onChange={(e) => update('monthlyLimit', e.target.value)} />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Log Retention (Days)</label><HelpHint label="Log Retention (Days)"/>
              <select className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm" value={settings.retentionDays} onChange={(e) => update('retentionDays', e.target.value)}>
                <option value={30}>30 Days</option>
                <option value={90}>90 Days (Standard)</option>
                <option value={365}>1 Year (Compliance)</option>
              </select>
            </div>
          </div>
        </SettingsSection>

        {/* Masking & PII */}
        <SettingsSection title="Data Masking" icon={FiEye}>
          <div className="space-y-2">
            <ToggleField label="Mask Recipient Emails" description="Hide PII in operational logs" enabled={settings.maskEmails} onToggle={() => update('maskEmails', !settings.maskEmails)} />
            <ToggleField label="Mask App Secrets" description="Obfuscate keys in app config" enabled={settings.maskSecrets} onToggle={() => update('maskSecrets', !settings.maskSecrets)} />
            <ToggleField label="Mask LLM Keys" description="Protect AI provider credentials" enabled={settings.maskLLM} onToggle={() => update('maskLLM', !settings.maskLLM)} />
          </div>
        </SettingsSection>

        {/* Regional Defaults */}
        <SettingsSection title="Regional Defaults" icon={FiGlobe}>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Default Timezone</label><HelpHint label="Default Timezone"/>
              <select className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm" value={settings.timezone} onChange={(e) => update('timezone', e.target.value)}>
                <option>AEST (UTC+10)</option>
                <option>UTC</option>
                <option>EST (UTC-5)</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Default Sender Email</label><HelpHint label="Default Sender Email"/>
              <input type="email" className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-mono" value={settings.senderEmail} onChange={(e) => update('senderEmail', e.target.value)} />
            </div>
          </div>
        </SettingsSection>

        {/* Feature Defaults */}
        <SettingsSection title="Feature Defaults" icon={FiToggleRight}>
          <div className="space-y-4">
            <ToggleField label="Chatbot Visibility" description="Default state for new apps" enabled={settings.defaultChatbot} onToggle={() => update('defaultChatbot', !settings.defaultChatbot)} />
            <div className="space-y-1.5">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Default OTP Mode</label><HelpHint label="Default OTP Mode"/>
              <select className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm" value={settings.otpMode} onChange={(e) => update('otpMode', e.target.value)}>
                <option>External Apps Script</option>
                <option>BPA Managed (v1.2+)</option>
              </select>
            </div>
          </div>
        </SettingsSection>
      </div>
    </div>
  );
};

export default Settings;